import React, { useEffect } from "react";
import { Modal } from "components/common";
import {
  StyledFileListWrapper,
  StyledFileManagementContainer,
  StyledFilePage,
  StyledFilePasteModal,
  StyledFilePasteModalTypography,
  StyledHeadline,
  StyledImageAdd,
  StyledMediaContainer,
  StyledNoDataTypography,
  StyledPasteArea,
} from "./styled";
import { FormikHelpers, FormikProps, useFormik } from "formik";
import { useHistory } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { PageLevel, useConsoleState } from "providers/ConsoleStateProvider";
import {
  CreateFileResourceInput,
  FileResource,
  ResourceType,
} from "types/graphqlapi";
import { Storage } from "aws-amplify";
import { useLoadingState } from "providers/LoadingProvider";
import {
  createInterviewResoucesMutation,
  deleteInterviewResouceMutation,
  updateInterviewResouceMutation,
} from "utils/apis/fileResource";
import { DisplayFileResource, useFileResource } from "./hook";
import { validationSchemaFileResourceFile } from "utils/validation";
import { ImagePresenter } from "components/common/ImagePresenter";
import { v4 as uuidv4 } from "uuid";
import { BackButton } from "components/parts/BackButton";
import { Typography } from "components/parts/Typography/Typography";
import {
  StyledTbodyTr,
  StyledTable,
  StyledTableCell,
  StyledTbody,
  StyledTbodyTd,
  StyledThead,
  StyledTheadTh,
  StyledTheadTr,
  TableColumnProps,
} from "components/templates/Table/styled";
import { StyledNodataTypography } from "pages/InterviewManagement/InterviewList/styled";
import { FileManageModal } from "pages/FileManagement/FileManageModal";

import { newColors } from "constants/newColors";
import { usePageHeader } from "hooks/usePageHeader";
import { InterviewTypeIcon } from "components/parts/InterviewTypeIcon";
import { VideoPresenter } from "components/common/VideoPresenter";

export type FileResourceFormValueType = {
  file?: File;
  fileName: string;
  title: string;
  description: string;
};

const FILE_TABLE_COLUMN_LIST = [
  {
    columnId: "preview",
    columnType: "media",
    textAlign: "center",
    minWidth: "10em",
    width: "600px",
    text: "プレビュー",
  },
  {
    columnId: "title",
    columnType: "typography",
    textAlign: "left",
    minWidth: "15em",
    width: "800px",
    text: "タイトル",
  },
  {
    columnId: "description",
    columnType: "typography",
    textAlign: "left",
    minWidth: "15em",
    width: "1000px",
    text: "説明",
  },
  {
    columnId: "typeDescription",
    columnType: "typography",
    textAlign: "center",
    minWidth: "5em",
    width: "300px",
    text: "種類",
  },
] as TableColumnProps[];

export const FileManagement: React.FC = () => {
  const history = useHistory();
  const { setLoading } = useLoadingState();
  const {
    selectedGroupId,
    selectedSubjectId,
    setPageLevel,
    selectedSubjectType,
  } = useConsoleState();
  const { pageHeader } = usePageHeader(selectedSubjectId);
  const { fileResourceList, fetchFileResource } = useFileResource();

  const [selectedFile, setSelectedFile] = React.useState<DisplayFileResource>();

  useEffect(() => {
    (async () => {
      setLoading(true);
      setPageLevel(PageLevel.fileAndScenarioAndMonitor);
      await fetchFileResource(selectedSubjectId);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * S3にアップロードする際のURLを決定する（初回時のみ）
   * @param groupId
   * @param subjectId
   * @param fileName：
   * @returns
   */
  const craeteS3Url = (
    groupId: string,
    subjectId: string,
    fileName: string
  ) => {
    return `${groupId}/${subjectId}/${fileName}`;
  };

  /**
   * S3にファイルをアップロードする
   * @param url
   * @param file
   * @param contentType
   * @returns
   */
  const uploadFileToS3 = (
    url: string,
    file: File | undefined,
    contentType: string
  ) => {
    if (file === undefined) {
      return;
    }
    return Storage.put(url, file, { contentType: contentType });
  };

  /**
   * DynamoDBへインサートするファイルの情報を整理する
   * @param values
   */
  const createFileInformation = (
    values: FileResourceFormValueType
  ): CreateFileResourceInput => {
    const id = uuidv4();
    const targetFileType = values.file?.type.includes("video")
      ? ResourceType.VIDEO
      : ResourceType.IMAGE;

    const fileInformaton: CreateFileResourceInput = {
      id: id,
      subject_id: selectedSubjectId,
      sort_index: 1,
      file_name: values.fileName,
      title: values.title,
      type: targetFileType,

      url: craeteS3Url(
        selectedGroupId,
        selectedSubjectId,
        `${id}/${values.fileName}`
      ),
      description: values.description,
      groups: selectedGroupId,
    };
    return fileInformaton;
  };

  /**
   * ファイルをS3にアップロードし、情報をDynamoDBへ挿入する
   * @param values
   * @returns
   */
  const saveFile = async (
    values: FileResourceFormValueType,
    formikHelpers: FormikHelpers<FileResourceFormValueType>
  ): Promise<void> => {
    if (!values.title) {
      return;
    }
    setLoading(true);
    try {
      // 保存するファイルの情報を整理する
      const fileInformation = createFileInformation(values);

      const targetContentType = acceptedFiles[0].type.includes("video")
        ? "application/octet-stream"
        : acceptedFiles[0].type;

      // S3へファイルを保存する
      const uploadFileResult = await uploadFileToS3(
        fileInformation.url,
        acceptedFiles[0],
        targetContentType
      );
      if (uploadFileResult && !uploadFileResult.key) {
        alert("エラーが発生しました。");
        history.replace("/auth/login");
      }

      // DynamoDBへ情報を保存する
      const saveFileResult = await createInterviewResoucesMutation(
        fileInformation
      );
      if (saveFileResult.errors) {
        console.error(saveFileResult.errors[0].message);
        alert("エラーが発生しました。");
        history.replace("/auth/login");
      }

      alert("ファイルの保存に成功しました");
    } catch (error) {
      alert("エラーが発生しました。");
      history.replace("/auth/login");
    } finally {
      formikHelpers.resetForm();
      setSelectedFile(undefined);
      await fetchFileResource(selectedSubjectId);
      setLoading(false);
    }
  };

  const editFileHandler = async (
    values: FileResourceFormValueType,
    formikHelpers: FormikHelpers<FileResourceFormValueType>
  ): Promise<void> => {
    setLoading(true);
    try {
      await updateInterviewResouceMutation({
        id: selectedFile?.id as string,
        title: values.title,
        description: values.description,
      });
    } catch (error) {
      alert("エラーが発生しました。");
      history.replace("/auth/login");
    } finally {
      formikHelpers.resetForm();
      setSelectedFile(undefined);
      await fetchFileResource(selectedSubjectId);
      setLoading(false);
    }
  };

  const fileFormik: FormikProps<FileResourceFormValueType> = useFormik({
    initialValues: {
      fileName: selectedFile?.file_name || "",
      title: selectedFile?.file_name || "",
      description: selectedFile?.description || "",
    },
    validationSchema: validationSchemaFileResourceFile,
    onSubmit: selectedFile?.id ? editFileHandler : saveFile,
  });

  /**
   * S3のファイルを削除する
   * @param url
   * @returns
   */
  const deleteFileOnS3 = (url: string) => {
    return Storage.remove(url);
  };

  /**
   * 削除ボタン機能
   */
  const onDeleteButtonHandler = async (interviewResouce: FileResource) => {
    const res = confirm("選択されたファイルを削除します");
    if (!res) {
      return;
    }
    setLoading(true);
    try {
      // S3からファイルを削除する
      await deleteFileOnS3(interviewResouce.url);
      // DBからファイル情報を削除する
      await deleteInterviewResouceMutation({ id: interviewResouce.id });
      alert("ファイルの削除に成功しました");
    } catch (error) {
      console.error(error);
      alert("エラーが発生しました。");
      history.replace("/auth/login");
    } finally {
      setSelectedFile(undefined);
      fileFormik.setFieldValue("file", {} as File);
      fileFormik.setFieldValue("fileName", "");
      fileFormik.setFieldValue("title", "");
      fileFormik.setFieldValue("description", "");
      fileFormik.setFieldTouched("file", false);
      fileFormik.setFieldTouched("fileName", false);
      fileFormik.setFieldTouched("title", false);
      fileFormik.setFieldTouched("description", false);
      await fetchFileResource(selectedSubjectId);
      setLoading(false);
    }
  };

  const closeHandler = () => {
    setSelectedFile(undefined);
    fileFormik.setFieldValue("file", {} as File);
    fileFormik.setFieldValue("fileName", "");
    fileFormik.setFieldValue("title", "");
    fileFormik.setFieldValue("description", "");
    fileFormik.setFieldTouched("file", false);
    fileFormik.setFieldTouched("fileName", false);
    fileFormik.setFieldTouched("title", false);
    fileFormik.setFieldTouched("description", false);
  };

  /**
   * ドラッグドロップ用
   */
  const [isDragToBrowser, setIsDragToBrowser] = React.useState(false);
  const [isDragToDropArea, setIsDragToDropArea] = React.useState(false);

  const onDropAccepted = (acceptedFiles: File[]) => {
    if (!acceptedFiles[0].type.match(/image\/jpeg|image\/png|video/)) {
      alert("画像は以下の形式のファイルのみ利用できます。\nJPEG, PNG");
      return;
    }

    fileFormik.setFieldValue("fileName", acceptedFiles[0].name);
    fileFormik.setFieldValue("title", acceptedFiles[0].name);
    fileFormik.setFieldValue("file", acceptedFiles[0]);
    setSelectedFile({} as DisplayFileResource);
  };

  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    onDropAccepted,
    multiple: false,
  });

  return (
    <>
      <StyledFilePage onDragEnter={() => setIsDragToBrowser(true)}>
        {isDragToBrowser && (
          <Modal
            width={"70%"}
            height={"70%"}
            closeHandler={() => setIsDragToBrowser(false)}
            onDrop={() => setIsDragToBrowser(false)}
          >
            <StyledFilePasteModal>
              <StyledFilePasteModalTypography text="画像を登録" size="large" />
              <StyledPasteArea
                {...getRootProps({
                  onDragOver: () => setIsDragToDropArea(true),
                  onDragLeave: () => setIsDragToDropArea(false),
                })}
                style={
                  isDragToDropArea
                    ? { border: `solid 3px ${newColors.blue}` }
                    : {}
                }
              >
                <StyledImageAdd />
                <StyledFilePasteModalTypography text="ファイルをここにドロップしてください" />
              </StyledPasteArea>
            </StyledFilePasteModal>
          </Modal>
        )}
        <input
          {...getInputProps()}
          accept=".jpeg,.jpg,.png,image/jpeg,image/png,video/*"
        />
        <BackButton />
        <StyledFileManagementContainer>
          <StyledHeadline>
            <InterviewTypeIcon interviewType={selectedSubjectType} size={24} />
            <Typography
              text={`${pageHeader.subjectName}のファイル`}
              size="xxlarge"
              weight={"bold"}
            />
          </StyledHeadline>
          <StyledFileListWrapper>
            <StyledTable>
              <StyledThead>
                <StyledTheadTr>
                  {FILE_TABLE_COLUMN_LIST.map((column) => (
                    <StyledTheadTh
                      key={`MonitorTableHeaderColumn${column.columnId}`}
                      textAlign={column.textAlign}
                      minWidth={column.minWidth}
                      width={column.width}
                    >
                      <StyledTableCell minWidth={column.minWidth}>
                        <Typography text={column.text} />
                      </StyledTableCell>
                    </StyledTheadTh>
                  ))}
                </StyledTheadTr>
              </StyledThead>
              <StyledTbody>
                {fileResourceList.length === 0 ? (
                  <tr>
                    <td>
                      <StyledNodataTypography text="ファイルをドロップしてください" />
                    </td>
                  </tr>
                ) : (
                  fileResourceList.map((resource) => (
                    <StyledTbodyTr
                      key={resource?.id}
                      onClick={() => {
                        setSelectedFile(resource);
                        fileFormik.setFieldValue("file", resource);
                        fileFormik.setFieldValue(
                          "fileName",
                          resource.file_name
                        );
                        fileFormik.setFieldValue("title", resource.title);
                        fileFormik.setFieldValue(
                          "description",
                          resource.description
                        );
                      }}
                    >
                      {FILE_TABLE_COLUMN_LIST.map((column) => (
                        <StyledTbodyTd
                          key={`${column.columnId}Of${resource?.id}`}
                          textAlign={column.textAlign}
                          minWidth={column.minWidth}
                          width={column.width}
                        >
                          <StyledTableCell minWidth={column.minWidth}>
                            {column.columnType === "typography" ? (
                              (() => {
                                const key =
                                  column.columnId as keyof typeof resource;
                                const resourceContent =
                                  resource !== undefined ? resource[key] : "";
                                if (typeof resourceContent === "string")
                                  return <Typography text={resourceContent} />;
                              })()
                            ) : column.columnType === "media" ? (
                              <StyledMediaContainer>
                                {resource.type === ResourceType.IMAGE ? (
                                  <ImagePresenter
                                    src={resource.src}
                                    maxHeight={"15vh"}
                                  />
                                ) : (
                                  <VideoPresenter
                                    src={resource.src}
                                    maxHeight={"15vh"}
                                  />
                                )}
                              </StyledMediaContainer>
                            ) : (
                              ""
                            )}
                          </StyledTableCell>
                        </StyledTbodyTd>
                      ))}
                    </StyledTbodyTr>
                  ))
                )}
              </StyledTbody>
            </StyledTable>
          </StyledFileListWrapper>
          {fileResourceList.length !== 0 && (
            <StyledNoDataTypography text="画面にファイルをドロップして追加" />
          )}
        </StyledFileManagementContainer>
      </StyledFilePage>
      {selectedFile && (
        <FileManageModal
          selectedFile={selectedFile}
          fileFormik={fileFormik}
          onDeleteButtonHandler={onDeleteButtonHandler}
          closeHandler={closeHandler}
        />
      )}
    </>
  );
};
